import { useEffect, useRef, useState } from 'react';
import { useTranslations } from 'use-intl';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { LoadingState } from '@leanin/ui';
import { AuthLayout } from '@/components/auth/AuthLayout';
import { EntryJourneyScreen } from '@/components/entry-journeys';
import { RecoveryError, type RecoveryReason } from '@/components/auth/RecoveryError';
import { getApiClient } from '@/lib/api-client';
import {
  apiErrorCode,
  parseApiErrorCode,
  parseApiErrorDetails,
} from '@/lib/api-error';
import { consumeAuthIntent } from '@/lib/analytics/auth-intent';
import { trackLogin, trackSignUp } from '@/lib/analytics/events';
import { sessionQuery } from '@/lib/use-session';
import { takeAuthDrift } from '@/lib/queries/identity';
import { clearSignupRef, readSignupRef } from '@/lib/signup-ref-cookie';
import {
  clearDirectoryHint,
  clearEntryJourney,
  readEntryJourney,
} from '@/lib/entry-journey';
import { useJoinGroup } from '@/lib/mutations/groups';
import { navigateArgsForGroup } from '@/lib/group-url';
import {
  clearPostOnboardingDestination,
  destinationAfterSignup,
  writePostOnboardingDestination,
  type PostOnboardingGroup,
} from '@/components/onboarding/post-onboarding-destination';
import {
  clearPendingJoinGroup,
  readPendingJoinGroup,
} from '@/components/onboarding/pending-join-group';
import { readPendingInviteToken } from '@/components/onboarding/pending-invite';
import { returnPathSearchParam } from '@/lib/return-path';
import { Sentry } from '@/sentry';

// `next` is the page the member should land on instead of the generic /feed
// — straight away, or once onboarding finishes for a member who still needs
// it. The sign-in and signup forms forward it: the page the visitor chose to
// join from (`?next=` on the Join landing), by the email path and through the
// Google round-trip. Invites don't use it (new invitees sign up at
// /auth/signup with a pendingInviteToken). Validated as an in-app path so a
// crafted link can't route to an external host post-sign-in.
const finishingSearchSchema = z.object({
  next: returnPathSearchParam,
});

export const Route = createFileRoute('/auth/finishing')({
  validateSearch: finishingSearchSchema.parse,
  component: FinishingPage,
});

// Post-signup landing for both OAuth and email/password sign-ups. Calls
// POST /api/v1/auth/finish-signup and routes the user based on the server's
// onboarding completion signal:
//   - `needs_onboarding` → send to /onboarding/profile
//   - `onboarded` → send straight to /feed
//
// Reading `users.onboarding_completed_at` (not "did we just insert the
// row?") is what closes the SSO race: the Clerk `user.created` webhook
// frequently lands before this page calls finish-signup, so the legacy
// "row already exists ⇒ they're done" check shipped SSO sign-ups to /feed
// before they ever saw onboarding.
//
// This endpoint uses Clerk's `authenticateRequest` directly, which ignores
// dev-bypass — so a dev-bypass user reaching this page can't proceed.

// Bound the wait for Clerk session hydration before surfacing a recovery
// surface. A null `user` immediately after `isLoaded` flips is normal
// (Clerk reports loaded as soon as the SDK initializes; the session
// itself can take another tick to propagate from setActive). Bouncing
// instantly on null sends a freshly-signed-up visitor through the root
// index → /sign-in dead-end. 5s is a generous upper bound — typical
// hydration takes < 200ms; anything longer is a real failure mode that
// deserves the recovery surface, not a silent bounce.
const SESSION_HYDRATION_MAX_WAIT_MS = 5_000;

function FinishingPage() {
  const t = useTranslations('Auth');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Use `useAuth()` over `useUser()` for the post-signup hydration gate:
  // `useAuth().isLoaded` flips once Clerk's SDK has fully initialised and
  // resolved the active session (`sessionId`). `useUser().user` lags a tick
  // — it hydrates from the session in a SECOND React render, so reading
  // `user === null` immediately after `setActive()` raced the cookie
  // propagation and we bounced to `/` mid-signup. The 5s timer below
  // becomes a defensive backstop, not the primary gate.
  const { isLoaded, isSignedIn, sessionId } = useAuth();
  const { next } = Route.useSearch();
  const joinGroup = useJoinGroup();
  const [recoveryReason, setRecoveryReason] = useState<RecoveryReason | null>(null);
  // Which chrome this screen wears, snapshotted at mount: the mutation clears
  // the entry journey as it completes, and the surface must not swap
  // mid-flight. An invite outranks a journey for the same reason it does in
  // the routing below — the invite names the concrete destination.
  const [chromeJourney] = useState(() =>
    readPendingInviteToken() !== null ? null : readEntryJourney(),
  );

  const mutation = useMutation({
    mutationFn: async () => {
      const client = getApiClient();
      // Invite redemption is server-driven by the verified email
      // (redeemInvitesForEmail) — nothing to pass from the client.
      const { data, error } = await client.POST('/api/v1/auth/finish-signup', {});
      if (error !== undefined || data === undefined) {
        const code = parseApiErrorCode(error);
        const details = parseApiErrorDetails(error);
        let reason: RecoveryReason = 'unknown';
        if (code === 'VALIDATION' && details?.user?.includes('no_email_address')) {
          reason = 'no_email_address';
        } else if (code === 'identity/email_in_use') {
          reason = 'email_in_use';
        } else if (code === 'identity/clerk_unavailable') {
          reason = 'clerk_unavailable';
        }
        throw { kind: 'recovery', reason } as const;
      }
      return data;
    },
    onSuccess: async (data) => {
      // Auth attribution: the upstream form stashed its intent + method
      // (SSO button before the off-site redirect, email signup before
      // navigating here). Consumed before the awaits so the marker is
      // one-shot even if the refetch below stalls. No marker (email login
      // fires inline, or the stash was unavailable) → no event; never
      // guess. The event itself fires after the session refetch so it can
      // carry the internal user_id — finish-signup just created the users
      // row, and the refetch is what makes its id readable.
      const authMarker = consumeAuthIntent();
      // The unauthenticated visit to /auth/{login,signup,password-reset}
      // cached a `null` session (those routes run `redirectIfSignedIn`).
      // Now that Clerk has an active session and the member row exists, we
      // need the next route guard's `ensureQueryData(sessionQuery)` call to
      // see the FRESH /me/context — otherwise it reads the cached null and
      // bounces straight back to /sign-in.
      //
      // `refetchQueries` invalidates AND awaits the in-flight fetch, unlike
      // `invalidateQueries` which only marks stale and lets the next reader
      // race the new Clerk session-cookie's propagation. The race
      // manifests as a 401 from /me/context → null context → _authed
      // bounces to /sign-in. Awaiting the refetch here closes the race.
      await queryClient.refetchQueries({ queryKey: sessionQuery.queryKey });
      // Drift-final account (deactivated past the self-reactivate window /
      // restricted): finish-signup 200s — it authenticates the Clerk JWT and
      // UPSERTs, deliberately blind to account standing — but the refetch
      // above got the discriminated 401 and has already dropped the Clerk
      // session. Short-circuit to the login page's reason banner instead of
      // continuing into claims/joins/navigation that would all race the
      // sign-out. This is the Google/OAuth landing path: sso-callback sends
      // every completed OAuth round-trip here, so without this check a
      // deactivated account signing in with Google dead-ends wordlessly.
      const refreshedCtx = queryClient.getQueryData(sessionQuery.queryKey);
      if (refreshedCtx === null || refreshedCtx === undefined) {
        const reason = takeAuthDrift();
        if (reason !== null) {
          void navigate({ to: '/sign-in', search: { reason }, replace: true });
          return;
        }
      }
      if (authMarker !== null) {
        const ctx = queryClient.getQueryData(sessionQuery.queryKey);
        (authMarker.intent === 'signup' ? trackSignUp : trackLogin)({
          method: authMarker.method,
          user_id: ctx?.session.userId ?? null,
        });
      }
      // Best-effort referral attribution. The session is live and the member
      // row exists, so the claim authenticates. Stamping a code grants nothing,
      // so a failure must never block or fail the finishing flow — claim once,
      // then clear the cookie regardless of the result. When the claimed code
      // is a group join link the response names the advertised group, which
      // becomes the post-signup landing surface.
      const claimedGroup = await claimSignupRef();
      // The group this signup asked to join, from either signal: the join
      // link they arrived through (claim) or the group page's
      // Sign-in-to-Join click (sessionStorage intent). Joined here through
      // the same joinPolicy-dispatched endpoint the page's button uses:
      // open groups grant membership, approval groups queue a join
      // request, invite-only groups refuse (403). Best-effort like the
      // claim — a failure leaves the visitor on the group page where the
      // normal join CTA is available — with ONE exception: a group whose
      // join requires the intake form refuses this bare join with
      // `groups/intake_required`. That member still has a request to make,
      // so the refusal is carried forward as `needsJoin` and the group page
      // opens its Request-to-Join modal (`?join=1`) instead of silently
      // dropping the referral. The intent is cleared unconditionally so a
      // stale one can't join a later signup into a group it never asked for.
      const pendingGroup = readPendingJoinGroup();
      const intendedGroup = claimedGroup ?? pendingGroup;
      clearPendingJoinGroup();
      let needsJoinForm = false;
      if (intendedGroup !== null) {
        try {
          await joinGroup.mutateAsync({
            id: intendedGroup.id,
            kind: intendedGroup.kind,
            joinMethod: 'post_signup',
          });
        } catch (err) {
          needsJoinForm = apiErrorCode(err) === 'groups/intake_required';
        }
      }
      if (data.status === 'onboarded') {
        // Already-onboarded users never consume the ad entry journey, so
        // drop it here — a stale journey lingering in sessionStorage must
        // not shape a later session in this tab (the same leak class as
        // the stale invite-token persona bug). The directory-hint marker is
        // the same class one step later: it belongs to the member whose
        // finish wrote it, not to whoever signs in on this tab next.
        clearEntryJourney();
        clearDirectoryHint();
        // `next` overrides the default /feed landing when an upstream flow
        // already knows where the member should land (the page the visitor
        // chose to sign in or join from). Validated
        // by the search schema as an in-app path. A full-document load
        // rather than a router hop: this tab's query cache was filled while
        // signed out (a public group or resource page the visitor signed in
        // from), and a client-side navigate could render that anonymous data
        // on the signed-in page. `replace` keeps /auth/finishing out of
        // browser history. A join-link group is the same idea sourced
        // from the claim instead of the URL.
        if (next !== undefined) {
          window.location.replace(next);
        } else if (intendedGroup !== null) {
          void navigate(
            navigateArgsForGroup(intendedGroup.kind, intendedGroup.id, undefined, {
              join: needsJoinForm,
            }),
          );
        } else {
          void navigate({ to: '/feed' });
        }
        return;
      }
      // needs_onboarding: route through onboarding. Where the member lands
      // once it finishes rides sessionStorage to the completion step: a
      // join-link / Sign up to Join group (no server-side trace), or else
      // `next`, the page they chose to join from — see
      // `destinationAfterSignup`. Nothing to land on clears the stash, so an
      // abandoned earlier signup in this tab can't redirect this member.
      //
      // `fromIntent`: the member clicked THIS group's own "Sign up to Join"
      // CTA, so the completion step lands them on the group even when the
      // signup rode a journey (/get-started stamps join-network on every
      // signup). The id match keeps a stale intent from an earlier abandoned
      // click from hijacking an ad-journey claim's directory landing.
      const destination = destinationAfterSignup({
        group:
          intendedGroup === null
            ? null
            : {
                kind: intendedGroup.kind,
                id: intendedGroup.id,
                ...(needsJoinForm ? { needsJoin: true as const } : {}),
                ...(pendingGroup !== null && pendingGroup.id === intendedGroup.id
                  ? { fromIntent: true as const }
                  : {}),
              },
        next,
      });
      if (destination === null) clearPostOnboardingDestination();
      else writePostOnboardingDestination(destination);
      // Ad-journey signups stay in the design-styled /onboarding/j/* steps
      // instead of the product onboarding: start-circle opens with its
      // Circle-page card, every other journey with the about step. The
      // organic /onboarding/profile flow remains the no-journey default —
      // and the fallback if a journey user ever lands there directly
      // (ProfileStep still honours the journey's persona preset).
      //
      // An invite always outranks an ad journey: the invite names a concrete
      // group + role, and the organic profile step is where its persona
      // preset lives. Without this, a stale journey from an abandoned ad
      // visit would hijack an invited signup in the same tab (the same leak
      // class as the old invite-token persona bug).
      const journey =
        readPendingInviteToken() !== null ? null : readEntryJourney();
      // Every journey opens with the about step; start-circle continues
      // from there into circle setup.
      if (journey !== null) {
        void navigate({ to: '/onboarding/j/about' });
      } else {
        void navigate({ to: '/onboarding/profile' });
      }
    },
    onError: (err: unknown) => {
      const recovery = err as { kind?: string; reason?: RecoveryReason };
      if (recovery.kind === 'recovery' && recovery.reason !== undefined) {
        setRecoveryReason(recovery.reason);
      } else {
        setRecoveryReason('unknown');
      }
    },
  });

  // Bounded wait for Clerk session hydration. `useAuth().isLoaded`
  // flips once Clerk's SDK has initialised; `sessionId` populates as
  // soon as `setActive()` completes propagation. The combo
  // (isLoaded && isSignedIn && sessionId) is the canonical "session
  // is live" gate per Clerk's React hook docs. Reading `useUser().user`
  // lags this by a tick — that's how we shipped the original race
  // (bouncing to `/` while `user` was still null).
  //
  // After SESSION_HYDRATION_MAX_WAIT_MS without a live session, surface
  // RecoveryError with the 'unknown' reason instead of bouncing silently.
  const sessionLive =
    isLoaded && isSignedIn === true && sessionId !== null && sessionId !== undefined;
  // Exactly once per mount. `mutation.isIdle` alone can't guarantee it: an
  // effect that runs twice before the next render (React StrictMode's dev
  // double-invoke) sees `isIdle` true both times, and the duplicate
  // onSuccess re-ran the one-shot claim/join/analytics and a second
  // full-document `window.location.replace(next)` that raced the first load.
  const finishStarted = useRef(false);
  useEffect(() => {
    if (!isLoaded) return;
    if (sessionLive) {
      if (!finishStarted.current) {
        finishStarted.current = true;
        mutation.mutate();
      }
      return;
    }
    const timer = setTimeout(() => {
      // Re-check inside the timer in case the session populated between
      // setTimeout schedule and fire (React batches effects).
      setRecoveryReason((prev) => prev ?? 'unknown');
    }, SESSION_HYDRATION_MAX_WAIT_MS);
    return () => clearTimeout(timer);
  }, [isLoaded, sessionLive, mutation, navigate]);

  if (recoveryReason !== null) {
    return <RecoveryError reason={recoveryReason} />;
  }

  // Ad-journey signups keep the journey chrome here. This screen sits between
  // the signup step and the first /onboarding/j step, so rendering AuthLayout's
  // editorial split-screen made the funnel flash a completely different surface
  // mid-flight. The stepper stays on "sign-up": the account exists but the
  // about step has not begun.
  if (chromeJourney !== null) {
    return (
      <EntryJourneyScreen journey={chromeJourney} current="sign-up">
        <LoadingState placement="page" label={t('finishing.label')} />
      </EntryJourneyScreen>
    );
  }

  return (
    <AuthLayout kicker={t('finishing.kicker')} headline={t('finishing.headline')}>
      <LoadingState placement="page" label={t('finishing.label')} />
    </AuthLayout>
  );
}

// Reads the `signup_ref` cookie (set on the signup route from `?ref=<code>`)
// and posts the attribution claim once, then deletes the cookie regardless of
// outcome. Best-effort: the code grants nothing, so a failure must never block
// or fail the finishing flow — it is reported (an attribution outage would
// otherwise silently undercount referrals) and the signup continues without
// the group landing. Returns the group a join link advertises (the server
// reports it whether or not the stamp took, so a returning visitor still lands
// on the group) or null for campaign codes and any failure.
async function claimSignupRef(): Promise<PostOnboardingGroup | null> {
  const code = readSignupRef();
  if (code === null) return null;
  try {
    const client = getApiClient();
    const { data, error } = await client.POST('/api/v1/signup-attribution/claim', {
      body: { code },
    });
    if (error !== undefined) {
      Sentry.captureMessage('signup attribution claim refused', {
        level: 'warning',
        tags: { flow: 'auth.signup_ref_claim', code: parseApiErrorCode(error) ?? 'unknown' },
      });
      return null;
    }
    return data.group ?? null;
  } catch (err) {
    Sentry.captureException(err, { tags: { flow: 'auth.signup_ref_claim' } });
    return null;
  } finally {
    clearSignupRef();
  }
}
